import { useEffect, useMemo, useState } from 'react';
import { Page, Card, DataTable, Link, Badge, Button, InlineStack, Tooltip, Modal, Text, BlockStack } from '@shopify/polaris';
import { getTableFooter } from '../../shared/components/functions.jsx';

export default function EmployeesPage() {
  const [items, setItems] = useState([]);
  const [saldosByUser, setSaldosByUser] = useState({});
  const [consumedByUser, setConsumedByUser] = useState({});
  const [saldosModalOpen, setSaldosModalOpen] = useState(false);
  const [selectedSaldosUser, setSelectedSaldosUser] = useState(null);
  const [selectedSaldos, setSelectedSaldos] = useState([]);
  const [selectedConsumed, setSelectedConsumed] = useState([]);
  const [consumedDetailsRows, setConsumedDetailsRows] = useState([]);
  const [saldosDetailsLoading, setSaldosDetailsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('age');
  const [sortDir, setSortDir] = useState('desc'); // default: oldest first (highest age)

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    async function fetchData() {
      try {
        const usersRes = await fetch('/api/users?include=hr');
        const usersData = await usersRes.json();
        setItems(Array.isArray(usersData) ? usersData : []);
      } catch (e) {
        console.error('Failed to fetch users', e);
        setItems([]);
      }
    }
    fetchData();
  }, []);

  // Fetch saldos for all users for the current year
  useEffect(() => {
    async function fetchAllSaldos() {
      if (items.length === 0) return;

      const saldosMap = {};
      // Fetch saldos for all active payroll users
      const activeUsers = items.filter((u) => !u.Inactief && u.OnPayroll);

      await Promise.all(
        activeUsers.map(async (user) => {
          try {
            const res = await fetch(`/api/users/${user.GebruikerID}/saldos/${currentYear}/latest`);
            if (res.ok) {
              const data = await res.json();
              saldosMap[user.GebruikerID] = data;
            }
          } catch (e) {
            console.error(`Failed to fetch saldos for user ${user.GebruikerID}`, e);
          }
        })
      );

      setSaldosByUser(saldosMap);
    }
    fetchAllSaldos();
  }, [items, currentYear]);

  // Fetch consumed leave days for all users for the current year
  useEffect(() => {
    async function fetchAllConsumed() {
      try {
        const res = await fetch(`/api/users/consumed/${currentYear}/all`);
        if (res.ok) {
          const data = await res.json();
          setConsumedByUser(data);
        }
      } catch (e) {
        console.error('Failed to fetch consumed days', e);
      }
    }
    fetchAllConsumed();
  }, [currentYear]);

  const toCurrency = (val) => (val != null && !Number.isNaN(val) ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(Number(val)) : '');

  const formatDays = (val) => {
    const n = Math.round((Number(val) || 0) * 10) / 10;
    return Number.isInteger(n) ? `${n}` : n.toFixed(1);
  };

  const formatHours = (val) => {
    const n = Math.round((Number(val) || 0) * 100) / 100;
    return Number.isInteger(n) ? `${n}` : n.toFixed(2);
  };

  const toConsumedHours = (entry) => {
    if (!entry) return 0;
    if (entry.ConsumedHours != null && !Number.isNaN(Number(entry.ConsumedHours))) return Number(entry.ConsumedHours);
    if (entry.Consumed != null && !Number.isNaN(Number(entry.Consumed))) return Number(entry.Consumed) * 8; // Backward compatibility with old "days" field
    return 0;
  };

  const formatTimeRange = (start, end) => {
    if (!start || !end) return '-';

    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const handleCloseSaldosModal = () => {
    setSaldosModalOpen(false);
    setSelectedSaldosUser(null);
    setSelectedSaldos([]);
    setSelectedConsumed([]);
    setConsumedDetailsRows([]);
    setSaldosDetailsLoading(false);
  };

  const handleOpenSaldosModal = async (user) => {
    setSelectedSaldosUser(user);
    setSelectedSaldos(Array.isArray(user._saldos) ? user._saldos : []);
    setSelectedConsumed(Array.isArray(user._consumed) ? user._consumed : []);
    setConsumedDetailsRows([]);
    setSaldosModalOpen(true);
    setSaldosDetailsLoading(true);

    try {
      const res = await fetch(`/api/users/${user.GebruikerID}/consumed/${currentYear}/details`);
      if (res.ok) {
        const data = await res.json();
        setSelectedConsumed(Array.isArray(data.summary) ? data.summary : []);
        setConsumedDetailsRows(Array.isArray(data.items) ? data.items : []);
      } else {
        console.error(`Failed to fetch consumed holiday details for user ${user.GebruikerID}`);
      }
    } catch (e) {
      console.error(`Failed to fetch consumed holiday details for user ${user.GebruikerID}`, e);
    }

    setSaldosDetailsLoading(false);
  };

  const computeAge = (birthday) => {
    if (!birthday) return null;
    const b = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - b.getFullYear();
    const m = today.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
    return age;
  };

  const yearsEmployed = (start) => {
    if (!start) return null;
    const s = new Date(start);
    const today = new Date();
    const diffMs = today.getTime() - s.getTime();
    return diffMs > 0 ? diffMs / (1000 * 60 * 60 * 24 * 365.25) : 0;
  };

  const enhanced = useMemo(() => {
    return (Array.isArray(items) ? items : [])
      .filter((u) => !u.Inactief && u.OnPayroll)
      .map((u) => ({
        ...u,
        _birthdayDate: u.Birthday ? new Date(u.Birthday) : null,
        _birthdayStr: u.Birthday ? new Date(u.Birthday).toLocaleDateString() : '',
        _age: computeAge(u.Birthday),
        _employmentStartStr: u.EmploymentStart ? new Date(u.EmploymentStart).toLocaleDateString() : '',
        _yearsEmployed: yearsEmployed(u.EmploymentStart),
        _saldos: saldosByUser[u.GebruikerID] || [],
        _consumed: consumedByUser[u.GebruikerID] || []
      }));
  }, [items, saldosByUser, consumedByUser]);

  const sorted = useMemo(() => {
    const arr = [...enhanced];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const getVal = (u) => {
        switch (sortKey) {
          case 'id':
            return Number(u.GebruikerID) || 0;
          case 'name':
            return (u.Gebruikernaam || '').toLowerCase();
          case 'email':
            return (u.Email || '').toLowerCase();
          case 'role':
            return (u.Gebruikertype || u.UserType || '').toString().toLowerCase();
          case 'birthday':
            return u._birthdayDate ? u._birthdayDate.getTime() : 0;
          case 'age':
            return u._age != null ? u._age : -1;
          case 'employmentStart':
            return u.EmploymentStart ? new Date(u.EmploymentStart).getTime() : 0;
          case 'yearsEmployed':
            return u._yearsEmployed != null ? u._yearsEmployed : -1;
          // case 'gross': // TODO: Re-enable later
          //   return Number(u.GrossMonth) || 0;
          case 'car':
            return Number(u.VehicleMonthlyCost) || 0;
          default:
            return 0;
        }
      };
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
    return arr;
  }, [enhanced, sortKey, sortDir]);

  const saldoSummaryRows = useMemo(() => {
    const relevantSaldoTypes = [1, 3, 6, 4, 5];
    const consumedMap = {};
    const consumedNameMap = {};

    (selectedConsumed || []).forEach((c) => {
      consumedMap[c.VerlofSaldoTypeID] = toConsumedHours(c);
      consumedNameMap[c.VerlofSaldoTypeID] = c.SaldoType;
    });

    const saldoMap = {};
    (selectedSaldos || []).forEach((s) => {
      if (relevantSaldoTypes.includes(s.VerlofSaldoTypeID)) {
        saldoMap[s.VerlofSaldoTypeID] = s;
      }
    });

    return relevantSaldoTypes
      .filter((typeId) => saldoMap[typeId] || consumedMap[typeId])
      .map((typeId) => {
        const saldo = saldoMap[typeId];
        const total = saldo?.Saldo ?? 0;
        const consumedHours = consumedMap[typeId] || 0;
        const remaining = total - consumedHours;
        const remainingTone = remaining < 0 ? 'critical' : remaining <= 8 ? 'warning' : 'success';

        return [saldo?.SaldoType || consumedNameMap[typeId] || `Type ${typeId}`, formatHours(consumedHours), formatHours(total), <Badge tone={remainingTone}>{formatHours(remaining)}h</Badge>];
      });
  }, [selectedSaldos, selectedConsumed]);

  const consumedDetailsRowsWithMonthHeaders = useMemo(() => {
    const monthFormatter = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
    const groups = new Map();

    (consumedDetailsRows || []).forEach((item) => {
      const rawDate = item.Date ? new Date(item.Date) : null;
      const isValidDate = rawDate && !Number.isNaN(rawDate.getTime());
      const monthKey = isValidDate ? `${rawDate.getFullYear()}-${String(rawDate.getMonth() + 1).padStart(2, '0')}` : 'unknown';

      if (!groups.has(monthKey)) {
        groups.set(monthKey, {
          key: monthKey,
          sortKey: isValidDate ? rawDate.getTime() : Number.MAX_SAFE_INTEGER,
          label: isValidDate ? monthFormatter.format(rawDate) : 'Unknown date',
          totalDays: 0,
          totalHours: 0,
          rows: []
        });
      }

      const currentGroup = groups.get(monthKey);
      const itemDays = Number(item.Days) || 0;
      const itemHours = Number(item.Hours) || 0;

      currentGroup.totalDays += itemDays;
      currentGroup.totalHours += itemHours;
      currentGroup.rows.push([
        item.Date ? new Date(item.Date).toLocaleDateString() : '',
        item.SaldoType || item.JobTypeName || '',
        formatDays(itemDays),
        item.Hours != null ? formatHours(itemHours) : '-',
        formatTimeRange(item.PlannedStartTime, item.PlannedEndTime)
      ]);
    });

    return Array.from(groups.values())
      .sort((a, b) => a.sortKey - b.sortKey)
      .flatMap((group) => {
        const monthHeaderRow = [
          <Text as="span" variant="headingSm">
            {group.label}
          </Text>,
          <Text as="span" tone="subdued">
            Total: {formatDays(group.totalDays)} days / {formatHours(group.totalHours)}h
          </Text>,
          '',
          '',
          ''
        ];

        return [monthHeaderRow, ...group.rows];
      });
  }, [consumedDetailsRows]);

  // Format saldos as badges with consumed days
  // Only show Verlofdagen (1), Ancieniteit (3), ADV (6), RecupFD (4), and Recup Verlof (5) with consumed info
  // Display format: remaining/total (e.g., "Ver: 15/20")
  const formatSaldos = (saldos, consumed) => {
    if (!saldos || saldos.length === 0) {
      return <Badge tone="attention">No saldos</Badge>;
    }

    // Create a map of consumed days by VerlofSaldoTypeID
    const consumedMap = {};
    if (consumed && consumed.length > 0) {
      consumed.forEach((c) => {
        consumedMap[c.VerlofSaldoTypeID] = toConsumedHours(c);
      });
    }

    // Only show relevant saldo types: 1 (Verlofdagen), 3 (Ancieniteit), 6 (ADV), 4 (RecupFD), 5 (Recup Verlof)
    const relevantSaldoTypes = [1, 3, 6, 4, 5];
    const filteredSaldos = saldos.filter((s) => relevantSaldoTypes.includes(s.VerlofSaldoTypeID));

    if (filteredSaldos.length === 0) {
      return <Badge tone="attention">No tracked saldos</Badge>;
    }

    return (
      <InlineStack gap="100" wrap={false}>
        {filteredSaldos.map((s) => {
          const total = s.Saldo ?? 0;
          const consumedHours = consumedMap[s.VerlofSaldoTypeID] || 0;
          const remaining = total - consumedHours;
          const abbrev = s.VerlofSaldoTypeID === 4 ? 'RecupFD' : s.VerlofSaldoTypeID === 5 ? 'RecupV' : s.SaldoType?.substring(0, 3);

          return (
            <Tooltip key={s.VerlofSaldoTypeID} content={`${s.SaldoType}: ${formatHours(remaining)}h left of ${formatHours(total)}h (consumed: ${formatHours(consumedHours)}h)`}>
              <Badge tone={remaining < 0 ? 'critical' : 'info'}>
                {abbrev}: {formatHours(remaining)}/{formatHours(total)}h
              </Badge>
            </Tooltip>
          );
        })}
      </InlineStack>
    );
  };

  const renderSaldosCell = (user) => (
    <button
      type="button"
      onClick={() => handleOpenSaldosModal(user)}
      title={`Open holiday overview for ${user.Gebruikernaam || user.Gebruiker || user.GebruikerID}`}
      style={{
        background: 'none',
        border: 0,
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        textAlign: 'left'
      }}
    >
      {formatSaldos(user._saldos, user._consumed)}
    </button>
  );

  const rows = sorted.map((u) => [
    <Link removeUnderline url={'/employees/' + u.GebruikerID}>
      {u.GebruikerID}
    </Link>,
    u.Gebruikernaam,
    u.Email,
    u.Gebruikertype || u.UserType,
    u._birthdayStr,
    u._age != null ? `${u._age}` : '',
    u._employmentStartStr,
    u._yearsEmployed != null ? `${u._yearsEmployed.toFixed(1)}` : '',
    // toCurrency(u.GrossMonth), // TODO: Re-enable later
    toCurrency(u.VehicleMonthlyCost),
    renderSaldosCell(u),
    u.Inactief ? <Badge tone="critical">Inactive</Badge> : <Badge tone="success">Active</Badge>
  ]);

  const totals = useMemo(() => {
    const n = sorted.length || 1;
    const sumGross = sorted.reduce((acc, u) => acc + (Number(u.GrossMonth) || 0), 0);
    const sumCar = sorted.reduce((acc, u) => acc + (Number(u.VehicleMonthlyCost) || 0), 0);
    const ages = sorted.map((u) => (u._age != null ? u._age : null)).filter((x) => x != null);
    const avgAge = ages.length > 0 ? ages.reduce((a, b) => a + b, 0) / ages.length : 0;
    const avgYears = sorted.reduce((acc, u) => acc + (u._yearsEmployed || 0), 0) / n;
    return {
      sumGross,
      sumCar,
      avgYears,
      avgAge
    };
  }, [sorted]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'age' ? 'desc' : 'asc');
    }
  };

  const SortHeader = ({ label, k }) => (
    <Button size="micro" variant="tertiary" onClick={() => toggleSort(k)}>
      <b>
        {label}
        {sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
      </b>
    </Button>
  );

  return (
    <Page title="Employees" fullWidth>
      <Modal
        open={saldosModalOpen}
        onClose={handleCloseSaldosModal}
        title={selectedSaldosUser ? `${selectedSaldosUser.Gebruikernaam || selectedSaldosUser.Gebruiker} holiday overview (${currentYear})` : `Holiday overview (${currentYear})`}
        size="large"
        secondaryActions={[
          {
            content: 'Close',
            onAction: handleCloseSaldosModal
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {saldosDetailsLoading ? (
              <Text as="p" tone="subdued">
                Loading holiday overview...
              </Text>
            ) : (
              <>
                <Text as="h3" variant="headingSm">
                  Saldos summary
                </Text>
                {saldoSummaryRows.length === 0 ? (
                  <Text as="p" tone="subdued">
                    No saldo data found for this employee in {currentYear}.
                  </Text>
                ) : (
                  <DataTable
                    increasedTableDensity
                    columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                    headings={['Saldo type', 'Taken (hours)', 'Total (hours)', 'Remaining (hours)']}
                    rows={saldoSummaryRows}
                  />
                )}

                <Text as="h3" variant="headingSm">
                  Taken days by date
                </Text>
                {consumedDetailsRowsWithMonthHeaders.length === 0 ? (
                  <Text as="p" tone="subdued">
                    No holidays were taken in {currentYear}.
                  </Text>
                ) : (
                  <DataTable
                    increasedTableDensity
                    columnContentTypes={['text', 'text', 'numeric', 'numeric', 'text']}
                    headings={['Date', 'Holiday type', 'Taken (days)', 'Hours', 'Time']}
                    rows={consumedDetailsRowsWithMonthHeaders}
                  />
                )}
              </>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Card>
        <DataTable
          verticalAlign="middle"
          stickyHeader
          increasedTableDensity
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'numeric', 'numeric', 'text', 'text']}
          headings={[
            <SortHeader label="Number" k="id" />,
            <SortHeader label="Name" k="name" />,
            <SortHeader label="Email" k="email" />,
            <SortHeader label="Role" k="role" />,
            <SortHeader label="Birthday" k="birthday" />,
            <SortHeader label="Age" k="age" />,
            <SortHeader label="Employment start" k="employmentStart" />,
            <SortHeader label="Years employed" k="yearsEmployed" />,
            // <SortHeader label="Gross/month" k="gross" />, // TODO: Re-enable later
            <SortHeader label="Car cost/month" k="car" />,
            <b>Saldos ({currentYear})</b>,
            <b>Status</b>
          ]}
          rows={rows}
          totals={[
            <b>Totals</b>,
            '',
            '',
            '',
            <b>Avg age</b>,
            <b>{totals.avgAge.toFixed(1)}</b>,
            <b>Avg years</b>,
            <b>{totals.avgYears.toFixed(1)}</b>,
            // <b>{toCurrency(totals.sumGross)}</b>, // TODO: Re-enable later
            <b>{toCurrency(totals.sumCar)}</b>,
            '',
            ''
          ]}
          showTotalsInFooter
          footerContent={getTableFooter(rows, page, setPage, 'employees')}
        />
      </Card>
    </Page>
  );
}
